"use client"

import Link from "next/link"
import { IconArrowRight, IconChevronRight, IconCircleCheck } from "@tabler/icons-react"
import { useTranslations } from "next-intl"
import { useFormatter } from "@/hooks/use-formatter"

import { ApolloPanel } from "@/components/ui/apollo-panel"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { useCurrency, formatCurrencyStatic } from "@/hooks/use-currency"

import type { Invoice } from "@/types"

interface OverdueInvoicesListProps {
  /** Overdue slice (up to 5 rows); `null` while unfetched. */
  data: Invoice[] | null
  isLoading: boolean
  /** Full overdue count behind the slice (list `meta.total`). */
  total?: number
  /** Extra classes merged onto the panel. */
  className?: string
}

/**
 * Chase-list rail — the invoices that are past their due date and still owe
 * money, newest debt last. The accountant's first action surface: every row
 * deep-links to the invoice so a payment can be recorded or a reminder sent.
 *
 * PRESENTATIONAL ONLY — the caller owns the `getOverdueInvoices()` fetch (see
 * ./finance-api) and the `invoices.view` gate, so an ungranted user never
 * renders this panel at all (hide-not-disable).
 *
 * Money: the REMAINING balance, not the invoice total — `balance_due` ships on
 * the index because the controller eager-loads payments; `total_amount` is the
 * defensive fallback for a payload that omits it.
 */
export function OverdueInvoicesList({
  data,
  isLoading,
  total,
  className,
}: OverdueInvoicesListProps) {
  const t = useTranslations("dashboard")
  const formatter = useFormatter()
  const { currency } = useCurrency()

  const rows = data ?? []
  const count = total ?? rows.length

  const money = (amount: number, invoiceCurrency?: string) =>
    formatCurrencyStatic(amount, invoiceCurrency || currency, { decimals: false })

  /** Remaining balance — never the gross total when a partial payment exists. */
  const remaining = (invoice: Invoice): number => {
    const balance = invoice.balance_due
    return typeof balance === "number" && Number.isFinite(balance)
      ? balance
      : invoice.total_amount
  }

  /**
   * `due_date` is a bare `Y-m-d`; anchoring it to local midnight keeps
   * "yesterday" from drifting a day in negative-offset timezones.
   */
  const dueLabel = (dueDate: string): string | null => {
    const date = new Date(`${dueDate}T00:00:00`)
    if (Number.isNaN(date.getTime())) return null
    return formatter.relativeTime(date)
  }

  const footer = (
    <div className="mt-3 border-t border-border pt-3">
      <Link
        href="/invoices"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        {t("overdueList.viewAll")}
        <IconArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  )

  if (isLoading) {
    return (
      <ApolloPanel eyebrow={t("overdueList.title")} className={className}>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-20 shrink-0" />
              <Skeleton className="h-4 flex-1" />
              <Skeleton className="h-4 w-20 shrink-0" />
            </div>
          ))}
        </div>
      </ApolloPanel>
    )
  }

  if (rows.length === 0) {
    return (
      <ApolloPanel eyebrow={t("overdueList.title")} className={className}>
        <div className="flex h-[180px] flex-col items-center justify-center text-center">
          <div className="mb-3 rounded-full bg-emerald-100 p-3 dark:bg-emerald-950">
            <IconCircleCheck className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            {t("overdueList.empty.title")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground/70">
            {t("overdueList.empty.body")}
          </p>
        </div>
        {footer}
      </ApolloPanel>
    )
  }

  return (
    <ApolloPanel
      eyebrow={t("overdueList.title")}
      actions={
        <Badge
          variant="outline"
          className="border-transparent bg-red-100 text-[10px] text-red-900 ring-1 ring-inset ring-red-200/50 dark:bg-red-950 dark:text-red-200"
        >
          {t("overdueList.count", { count })}
        </Badge>
      }
      className={className}
    >
      <div className="-mx-2 space-y-0.5">
        {rows.map((invoice) => {
          const due = invoice.due_date ? dueLabel(invoice.due_date) : null
          return (
            <Link
              key={invoice.id}
              href={`/invoices/${invoice.id}`}
              className="group flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
            >
              <span className="shrink-0 font-mono text-xs font-medium">
                {invoice.invoice_number}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">
                  {invoice.lead?.full_name ?? t("overdueList.noPatient")}
                </span>
                <span className="block truncate text-[11px] font-medium text-red-600 dark:text-red-400">
                  {due
                    ? t("overdueList.dueRelative", { when: due })
                    : t("overdueList.dueUnknown")}
                </span>
              </span>
              <span className="shrink-0 text-sm font-medium tabular-nums">
                {money(remaining(invoice), invoice.currency)}
              </span>
              <IconChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
            </Link>
          )
        })}
      </div>
      {footer}
    </ApolloPanel>
  )
}
